/**
 * BottomSheet (B10) — the mobile shell (§A.4): dusk glass, radius 28 28 0 0,
 * full width, snaps peek 130 · half 50 % (360–480; card min(460, 60 vh)) · full vh − 72 − the safe
 * areas (its top edge sits 72 below the top inset, clear of a notch or the status bar).
 *
 *  - Holds `panel` (the embedded places panel) and `card` (the embedded place card)
 *    as two stacked layers; the card replaces the list while a place is selected
 *    (the list stays mounted, so its scroll position and focus target survive).
 *  - Drag: the grip zone (top 24 px) and anything marked [data-ld-sheet-header]
 *    always drag; elsewhere the sheet drags until it is full, then the content
 *    scrolls (and drags down again from its top). Sideways gestures stay native
 *    (the chips row). [data-ld-sheet-nodrag] opts an element out.
 *  - The grip is a button: Enter / Space step up (full → half), ↑ / ↓ move a snap.
 *  - Focusing a text field below full lifts the sheet to full (room for the keyboard).
 *  - Arrives from below at the "ui" beat (640 settle). In a drive it steps aside
 *    (448 leave down) so the drive has the whole screen and the DriveHud the bottom
 *    edge; it returns with the card when the drive ends (640 settle). Inert while away.
 *    It manages this itself (data-ld-chrome="self"), so the layout's dim fallback skips it.
 */
import { useLayoutEffect, useMemo, useRef, type FocusEvent, type KeyboardEvent, type ReactNode } from "react";
import type { SheetSnap } from "@/contracts/store";
import { cx } from "@/design";
import type { UiState } from "./arrival";
import { gripSnap, nextSnap, sheetHeights, type SheetMode } from "./layoutMath";
import { OFFSCREEN, SheetController } from "./sheetController";

export interface BottomSheetProps {
  panel: ReactNode;
  card: ReactNode;
  mode: SheetMode;
  snap: SheetSnap;
  onSnap: (s: SheetSnap) => void;
  vh: number;
  /** env(safe-area-inset-top): the full snap stays below it (§A.4). */
  safeTop?: number;
  safeBottom: number;
  ui: UiState;
  /** A drive is on: the sheet steps aside below the viewport. */
  driving: boolean;
  reduced: boolean;
  /** Every frame: visible sheet height in px (safe area included) and its cover of the map (0 at half, 1 at full). */
  onHeight?: (px: number, cover: number) => void;
}

const GRIP_LABEL: Record<SheetMode, [expand: string, collapse: string]> = {
  panel: ["Show more places", "Show fewer places"],
  card: ["Show more details", "Show fewer details"],
};

export function BottomSheet({ panel, card, mode, snap, onSnap, vh, safeTop = 0, safeBottom, ui, driving, reduced, onHeight }: BottomSheetProps) {
  const heights = useMemo(() => sheetHeights(vh, mode, { top: safeTop, bottom: safeBottom }), [vh, mode, safeTop, safeBottom]);
  const sheetRef = useRef<HTMLDivElement>(null);
  const ctrl = useRef<SheetController | null>(null);

  // Live values for the controller's callbacks (never re-created).
  const live = useRef({ snap, onSnap, onHeight, reduced, enabled: true });
  live.current = { snap, onSnap, onHeight, reduced, enabled: ui !== "hidden" && !driving };

  // One controller per element; StrictMode's second mount builds a fresh one.
  useLayoutEffect(() => {
    const el = sheetRef.current;
    if (!el) return undefined;
    const c = new SheetController(
      el,
      { heights: sheetHeights(window.innerHeight || 844, "panel"), safe: 0 },
      {
        onRelease: (s) => live.current.onSnap(s),
        onHeight: (px, cover) => live.current.onHeight?.(px, cover),
        snap: () => live.current.snap,
        reduced: () => live.current.reduced,
        enabled: () => live.current.enabled,
      },
    );
    ctrl.current = c;
    return () => {
      c.destroy();
      if (ctrl.current === c) ctrl.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    ctrl.current?.setGeometry({ heights, safe: safeBottom });
  }, [heights, safeBottom]);

  // Where the sheet should be. The first placement jumps; everything after moves.
  const placed = useRef(false);
  const away = ui === "hidden" || driving;
  const target = away ? OFFSCREEN - safeBottom : heights[snap];
  useLayoutEffect(() => {
    const c = ctrl.current;
    if (!c) return;
    c.goTo(target, placed.current ? "animate" : "jump");
    placed.current = true;
  }, [target]);

  const onGripKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      onSnap(nextSnap(snap, e.key === "ArrowUp" ? 1 : -1));
    }
  };

  const onPanelFocus = (e: FocusEvent<HTMLDivElement>) => {
    const t = e.target;
    if (snap !== "full" && (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) onSnap("full");
  };

  const [expand, collapse] = GRIP_LABEL[mode];
  return (
    <div
      ref={sheetRef}
      className={cx("ld-glass", "ld-loc-layout-sheet")}
      data-mode={mode}
      data-snap={snap}
      data-away={away || undefined}
      data-ld-chrome="self"
      inert={away}
      style={{ height: heights.full + safeBottom }}
    >
      <div className="ld-loc-layout-sheet-handle" data-ld-sheet-handle="">
        <button
          type="button"
          className="ld-loc-layout-sheet-grip"
          aria-label={snap === "full" ? collapse : expand}
          onClick={() => onSnap(gripSnap(snap))}
          onKeyDown={onGripKey}
        >
          <span className="ld-loc-layout-sheet-bar" aria-hidden="true" />
        </button>
      </div>
      <div className="ld-loc-layout-sheet-body">
        <div
          className="ld-loc-layout-sheet-layer ld-loc-layout-sheet-layer--panel"
          data-active={mode === "panel"}
          inert={mode !== "panel"}
          onFocus={onPanelFocus}
        >
          {panel}
        </div>
        <div className="ld-loc-layout-sheet-layer ld-loc-layout-sheet-layer--card" data-active={mode === "card"} data-ld-card-slot="" inert={mode !== "card"}>
          {card}
        </div>
      </div>
    </div>
  );
}
